<template>
  <div>
    <div class="inline-block min-width-full rounded-lg shadow bg-white p-4 my-3">
      <!-- 기본 검색 섹션 -->
      <div class="mb-6">
        <div class="d-flex align-items-center mb-4 max-w-6xl" style="gap: 1rem;">
          <b-form-select
            style="width: 190px; min-width: 190px;"
            :options="selectSessionNumbers"
            v-model="search.sessionNumber"
          />
          <b-form-select
            style="width: 150px; min-width: 150px;"
            :options="selectRoundInSessions"
            v-model="search.roundInSession"
          />
          <b-input-group style="flex: 1;">
            <b-form-input
              id="inline-form-input-name"
              placeholder="이름 또는 학생코드, 전화번호를 입력하세요."
              v-model="search.keyword"
              v-on:keyup.enter="handleSearch"
            ></b-form-input>
            <b-input-group-append>
              <button class="btn btn-primary" v-on:click="handleSearch">검색</button>
            </b-input-group-append>
          </b-input-group>
        </div>

        <h2>총 {{totalRows}}명</h2>
      </div>

      <!-- 미션별 필터 섹션 (접을 수 있음) -->
      <div class="mb-6">
        <div class="bg-gray-50 rounded-lg p-6">
          <div class="flex justify-between items-center mb-6">
            <button
              @click="resetAllMissionFilters"
              class="px-4 py-2 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors font-medium"
            >
              모든 필터 초기화
            </button>
          </div>

          <!-- 필터 그리드 -->
          <div class="grid grid-cols-6 gap-4">
            <!-- 로그인 필터 -->
            <div class="bg-white rounded-lg border p-4">
              <div class="flex items-center justify-between mb-4">
                <h4 class="font-medium text-gray-800">로그인</h4>
                <button
                  @click="resetLoginFilter"
                  class="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  초기화
                </button>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-600 mb-2">상태</label>
                <b-form-select
                  :options="loginOptions"
                  v-model="search.loginStatus"
                  class="w-full"
                />
              </div>
            </div>

            <!-- 수업 시작 필터 (개선됨) -->
            <div class="bg-white rounded-lg border p-4">
              <div class="flex items-center justify-between mb-4">
                <h4 class="font-medium text-gray-800">수업 시작</h4>
                <button
                  @click="resetStartLessonFilter"
                  class="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  초기화
                </button>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-600 mb-2">상태</label>
                <b-form-select
                  :options="startLessonOptions"
                  v-model="search.startLessonStatus.status"
                  class="w-full"
                />
              </div>

              <!-- 수업 시작 O일 때 날짜 범위 선택 -->
              <div v-if="search.startLessonStatus.status === 'started'" class="mt-3">
                <label class="block text-sm font-medium text-gray-600 mb-2">수업 시작 기간</label>
                <div class="space-y-2">
                  <VueDatePicker
                    v-model="search.startLessonStatus.startDate"
                    format="yyyy-MM-dd"
                    :clearable="true"
                    placeholder="시작일"
                    auto-apply
                    class="w-full"
                  />
                  <VueDatePicker
                    v-model="search.startLessonStatus.endDate"
                    format="yyyy-MM-dd"
                    :clearable="true"
                    placeholder="종료일"
                    auto-apply
                    class="w-full"
                  />
                </div>
              </div>
            </div>

            <!-- 미션별 필터 -->
            <div v-for="mission in missionMilestones" :key="mission.id" class="bg-white rounded-lg border p-4">
              <div class="flex items-center justify-between mb-4">
                <h4 class="font-medium text-gray-800">{{ mission.name }}</h4>
                <button
                  @click="resetMissionFilter(mission.id)"
                  class="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  초기화
                </button>
              </div>

              <div>
                <!-- 상태 필터 -->
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-2">상태</label>
                  <b-form-select
                    :options="missionStatusOptions"
                    :model-value="getMissionFilterValue(mission.id, 'status')"
                    @input="updateMissionFilter(mission.id, 'status', $event)"
                    class="w-full"
                  />
                </div>

                <!-- 기간 조건 필터 -->
                <div class="my-2" v-if="getMissionFilterValue(mission.id, 'status') === 'completed'">
                  <label class="block text-sm font-medium text-gray-600 mb-2">기간 조건</label>
                  <b-form-select
                    :options="periodConditionOptions"
                    :model-value="getMissionFilterValue(mission.id, 'periodCondition')"
                    @input="updateMissionFilter(mission.id, 'periodCondition', $event)"
                    class="w-full"
                  />
                </div>

                <!-- 사용자 정의 기간 (조건부 표시) -->
                <div v-if="getMissionFilterValue(mission.id, 'periodCondition') === 'custom'">
                  <label class="block text-sm font-medium text-gray-600 mb-2">기간 설정</label>
                  <div class="flex items-center gap-2">
                    <VueDatePicker
                      :model-value="getMissionFilterValue(mission.id, 'customStartDate')"
                      @update:model-value="updateMissionFilter(mission.id, 'customStartDate', $event)"
                      format="yyyy-MM-dd"
                      :clearable="true"
                      placeholder="시작일"
                      auto-apply
                      class="flex-1"
                    />
                    <span class="text-gray-400">~</span>
                    <VueDatePicker
                      :model-value="getMissionFilterValue(mission.id, 'customEndDate')"
                      @update:model-value="updateMissionFilter(mission.id, 'customEndDate', $event)"
                      format="yyyy-MM-dd"
                      :clearable="true"
                      placeholder="종료일"
                      auto-apply
                      class="flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <p v-if="uploadButtonLoading" class="text-red-700 font-medium animate-bounce">
        업로드 진행 중 ...
      </p>

      <!-- 액션 버튼들 -->
      <div class="flex items-center gap-3 mb-6">
        <Button :type="ButtonTypes.blue" label="기준값 업데이트" :onClick="clickFileUpload" :loading="uploadButtonLoading" :disabled="uploadButtonLoading || isPreviousSession"/>
        <input
          type="file"
          ref="fileInput"
          class="hidden"
          accept=".xls,.xlsx"
          @change="upload"
        />
        <Button :type="ButtonTypes.purple" label="기준값 추가" :onClick="clickAddFileUpload" :loading="uploadButtonLoading" :disabled="uploadButtonLoading || isPreviousSession"/>
        <input
          type="file"
          ref="addFileInput"
          class="hidden"
          accept=".xls,.xlsx"
          @change="addUpload"
        />
        <Button :type="ButtonTypes.green" label="학습 관리 다운로드" :onClick="download" :loading="downloadButtonLoading" :disabled="downloadButtonLoading"/>
        <Button :type="ButtonTypes.red" label="일괄 삭제" :onClick="showDeletePopup" :loading="deleteButtonLoading" :disabled="deleteButtonLoading || isPreviousSession"/>

        <input
          type="file"
          ref="deleteFileInput"
          class="hidden"
          accept=".xls,.xlsx"
          @change="deleteAll"
        />
      </div>

      <!-- 경고 메시지 -->
      <div v-if="!isPreviousSession" class="mb-6 p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 font-medium">
        기준값 업데이트를 하게 되면 모든 데이터가 리셋됩니다. 주의해주세요.
      </div>
      <div v-else class="mb-6 p-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 font-medium">
        지난 회차로 보관된 학습 관리 데이터입니다. 실제 삭제된 데이터는 표시되지 않습니다.
      </div>
      
      <!-- 로딩 상태 -->
      <div v-if="isLoading" class="text-center py-12">
        <b-spinner label="Loading..." class="mb-3"/>
        <p class="text-gray-600">데이터를 불러오는 중입니다...</p>
      </div>

      <!-- 테이블 -->
      <div v-else class="overflow-x-auto">
        <div v-if="isLoading" class="text-center">
          <p>데이터를 불러오는 중입니다...</p>
          <b-spinner label="Loading..."/>
        </div>
        <b-table-simple v-else class="min-width-full">
          <b-thead class="text-center">
            <b-tr>
              <template v-for="(item, index) in headers" :key="index">
                <b-th>{{item}}</b-th>
              </template>
              <b-th v-if="!isPreviousSession"></b-th>
            </b-tr>
          </b-thead>
          <b-tbody class="text-center">
            <template v-for="(item, parentIndex) in learningSessions" :key="parentIndex">
              <b-tr>
                <td>{{ generateRowNumbers(parentIndex) }}</td>
                <td>{{ item.roundInSession}}</td>
                <td>{{ item.createdAt}}</td>
                <td>{{ item.accessCode }}</td>
                <td>{{ item.name }}</td>
                <td>{{ item.phoneNumber }}</td>
                <td class="text-center">{{ item.loginLog ? "O" : "X" }}</td>
                <td>{{ item.loginLog?.lastLoginAt ?? "" }}</td>
                <td>{{ item.startLessonLog?.lessonStartedAt ?? "" }}</td>
                <template  v-for="(logItem, logIndex) in item.missionLogs" :key="logIndex">
                  <td class="text-center">{{ logItem.status === "X" ? "" : logItem.status }}</td>
                  <template  v-for="(videoLog, videoLogIndex) in logItem.videoLogs" :key="videoLogIndex">
                    <td class="text-center">{{ videoLog.status }}</td>
                  </template>
                  <td>{{ logItem.endedAt }}</td>
                </template>
                <td>{{ item.confirmedKakaoName || "" }}</td>
                <td v-if="!isPreviousSession">
                  <div class="inline-flex gap-2 justify-center">
                    <Button :type="ButtonTypes.red" label="삭제" :on-click="() => {deleteItem(item.id)}"/>
                  </div>
                </td>
              </b-tr>
            </template>
          </b-tbody>
        </b-table-simple>
      </div>

      <!-- 페이지네이션 -->
      <div class="mt-6">
        <b-pagination
          align="center"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="pageSize"
          @page-click="changePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Button from "@/components/Button.vue";
import {ButtonTypes} from "@/util/enums";
import api from "@/api/api";
import customDate from "@/util/custom-date";
import moment from "moment";
import { mapActions } from 'vuex';

export default {
  computed: {
    ButtonTypes() {
      return ButtonTypes
    },
    // 로그인을 제외한 미션들만 필터링
    missionMilestones() {
      return this.allMilestones.filter(milestone => milestone.type === "LESSON_CONTENTS");
    },
    isPreviousSession() {
      return this.search.sessionNumber !== null;
    }
  },
  components: {Button},
  data() {
    return {
      headers: [],
      selectSessionNumbers: [{ value: null, text: "현재 회차" }],
      selectRoundInSessions: [],
      loginOptions: [
        { value: null, text: '로그인 전체' },
        { value: 'logged_in', text: '로그인 O' },
        { value: 'not_logged_in', text: '로그인 X' }
      ],
      startLessonOptions: [
        { value: null, text: '전체' },
        { value: 'started', text: '수업 시작 O' },
        { value: 'not_started', text: '수업시작 X' }
      ],
      missionStatusOptions: [
        { value: null, text: '전체' },
        { value: 'completed', text: '완료' },
        { value: 'watching', text: '수강중' },
        { value: 'incomplete', text: '미완료' }
      ],
      periodConditionOptions: [
        { value: null, text: '기간 조건 없음' },
        { value: 'within_deadline', text: '마감일 내 완료' },
        { value: 'after_deadline', text: '마감일 후 완료' },
        { value: 'custom', text: '사용자 정의 기간 내 완료' }
      ],
      selectOptions: [],
      showAdvancedFilters: false, // 고급 필터 표시 여부
      isModalOpen: false,
      learningSessions: [],
      isLoading: false,
      uploadButtonLoading: false,
      downloadButtonLoading: false,
      deleteButtonLoading: false,
      search: {
        keyword: "",
        sessionNumber: null,
        searchStartDate: moment().subtract(1, 'month').set({ hour: 0, minute: 0, second: 0}).toDate(),
        searchEndDate: null,
        milestone: null,
        roundInSession: null,
        loginStatus: null,
        startLessonStatus: {
          status: null,
          startDate: null,
          endDate: null
        },
        missionFilters: {}
      },
      allMilestones: [],
      file: null,
      currentPage: 1,
      pageSize: 30,
      totalPage: 0,
      totalRows: 0,
      loadingToast: null,
    }
  },
  watch: {
    'search.sessionNumber'(newVal, oldVal) {
      if (oldVal !== undefined) {
        this.currentPage = 1
        this.search.roundInSession = null
        this.updateQueryParams()
        this.get(1)
      }
    },
    'search.roundInSession'(newVal, oldVal) {
      // mounted에서 초기 설정 시에는 실행하지 않음
      if (oldVal !== undefined) {
        this.currentPage = 1
        this.updateQueryParams()
        this.get(1)
      }
    }
  },
  mounted() {
    // URL query params에서 상태 복원
    const query = this.$route.query
    if (query.page) {
      this.currentPage = parseInt(query.page)
    }
    if (query.keyword) {
      this.search.keyword = query.keyword
    }
    if (query.sessionNumber) {
      this.search.sessionNumber = parseInt(query.sessionNumber)
    }
    if (query.roundInSession) {
      this.search.roundInSession = parseInt(query.roundInSession)
    }
    
    this.learningSessions = []
    this.get(this.currentPage)
    this.getMilestones()
    this.getHeaders()
    this.getSessionNumbers()
    this.getRoundInSessions()
  },
  methods: {
    ...mapActions('modal', ['openModal']),

    generateRowNumbers(index) {
      return (this.currentPage - 1) * 30 + (index + 1)
    },

    // 고급 필터 토글
    toggleAdvancedFilters() {
      this.showAdvancedFilters = !this.showAdvancedFilters;
    },

    getHeaders() {
      api.learningSession.getHeaders((response) => {
        this.headers = ["번호", ...response.data.data]
      }, (error) => {
        console.log(error)
      })
    },

    getRoundInSessions() {
      api.learningSession.getRoundInSessions((response) => {
        this.selectRoundInSessions = [ {value: null, text: "전체"}, ...response.data.data.map(item => ({value: item, text: `${item}차 개강`})) ]
      }, (error) => {
        console.log(error)
      })
    },

    getSessionNumbers() {
      api.learningSession.getSessionNumbers((response) => {
        const { currentSessionNumber, previousSessionNumbers } = response.data.data
        this.selectSessionNumbers = [
          {
            value: null,
            text: currentSessionNumber ? `현재 회차 (${currentSessionNumber}회차)` : "현재 회차"
          },
          ...previousSessionNumbers.map(item => ({ value: item, text: `${item}회차 (지난 회차)` }))
        ]
      }, (error) => {
        console.log(error)
      })
    },

    // 미션 필터 초기화 - 반응성 문제 해결
    initializeMissionFilters() {
      const newFilters = {};
      this.missionMilestones.forEach(mission => {
        newFilters[mission.id] = {
          status: null,
          periodCondition: null,
          customStartDate: null,
          customEndDate: null
        };
      });
      this.search.missionFilters = newFilters
    },

    // 로그인 필터 초기화
    resetLoginFilter() {
      this.search.loginStatus = null;
      // 필터 적용
      this.currentPage = 1
      this.updateQueryParams()
      this.get(1);
    },

    // 수업 시작 필터 초기화 (새로 추가)
    resetStartLessonFilter() {
      this.search.startLessonStatus = {
        status: null,
        startDate: null,
        endDate: null
      };
      // 필터 적용
      this.currentPage = 1
      this.updateQueryParams()
      this.get(1);
    },

    // 특정 미션 필터 초기화 - 개선된 버전
    resetMissionFilter(missionId) {
      const resetFilter = {
        status: null,
        periodCondition: null,
        customStartDate: null,
        customEndDate: null
      };

      // 반응성을 위해 새로운 객체 생성
      const newFilters = { ...this.search.missionFilters };
      newFilters[missionId] = resetFilter;
      this.search.missionFilters = newFilters;

      // 필터 적용
      this.currentPage = 1
      this.updateQueryParams()
      this.get(1);
    },

    // 미션 필터 값 가져오기 헬퍼 메서드
    getMissionFilterValue(missionId, field) {
      return this.search.missionFilters[missionId] ? this.search.missionFilters[missionId][field] : null;
    },

    // 미션 필터 업데이트 헬퍼 메서드 - 개선된 버전
    updateMissionFilter(missionId, field, value) {
      // 새로운 객체 생성으로 반응성 보장
      const newFilters = { ...this.search.missionFilters };

      if (!newFilters[missionId]) {
        newFilters[missionId] = {
          status: null,
          periodCondition: null,
          customStartDate: null,
          customEndDate: null
        };
      }

      newFilters[missionId] = { ...newFilters[missionId], [field]: value };
      this.search.missionFilters = newFilters;
    },

    // 모든 미션 필터 초기화 - 개선된 버전 (수업 시작 필터 포함)
    resetAllMissionFilters() {
      // 로그인 필터 초기화
      this.search.loginStatus = null;

      // 수업 시작 필터 초기화
      this.search.startLessonStatus = {
        status: null,
        startDate: null,
        endDate: null
      };

      // 미션 필터 초기화
      const resetFilters = {};
      this.missionMilestones.forEach(mission => {
        resetFilters[mission.id] = {
          status: null,
          periodCondition: null,
          customStartDate: null,
          customEndDate: null
        };
      });

      this.search.missionFilters = resetFilters;

      // 필터 초기화 후 즉시 검색 실행
      this.currentPage = 1
      this.updateQueryParams()
      this.get(1);
    },

    getMilestones() {
      api.learningSession.getMilestones((response) => {
        // 기존 selectOptions 생성 (호환성을 위해 유지)
        const positiveOptions = response.data.map((item) => ({
          value: item.id.toString(),
          text: `${item.name} O`
        }));
        const negativeOptions = response.data.map((item) => ({
          value: `${item.id}-X`,
          text: `${item.name} X`
        }));

        this.allMilestones = response.data;
        this.selectOptions = [{value: null, text: "전체"}, ...positiveOptions, ...negativeOptions];
        this.initializeMissionFilters();
      })
    },

    getDate(date) {
      return customDate.stringFormat(date)
    },

    clickFileUpload() {
      this.openModal({
        type: 'confirm',
        isOpen: true,
        title: '기준값 업데이트',
        html: '<p class="text-rose-600 break-words">기준값 업데이트를 하게 되면 모든 데이터가 리셋됩니다.<br/> 진행하시겠습니까?</p>',
        confirm: () => {
          this.$refs.fileInput.click()
        },
      });
    },

    clickAddFileUpload() {
      this.$refs.addFileInput.click()
    },

    changePage(event, page) {
      this.currentPage = page
      this.updateQueryParams()
      this.get(page)
    },
    handleSearch() {
      this.currentPage = 1
      this.updateQueryParams()
      this.get(1)
    },
    updateQueryParams() {
      const query = {
        page: this.currentPage.toString()
      }
      
      if (this.search.keyword) {
        query.keyword = this.search.keyword
      }
      if (this.search.sessionNumber !== null) {
        query.sessionNumber = this.search.sessionNumber.toString()
      }
      if (this.search.roundInSession) {
        query.roundInSession = this.search.roundInSession.toString()
      }
      
      this.$router.replace({ query })
    },

    upload(event) {
      const uploadFile = event.target.files[0];
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel' // .xls
      ];

      if (!validTypes.includes(uploadFile.type)) {
        this.$toast.show('엑셀 형식만 올릴 수 있습니다.',
          {
            position: "top-right",
            duration: 2000
          });
        event.target.value = null;
        return
      } else {
        this.file = uploadFile
      }

      let formData = new FormData()
      formData.append('file', this.file, this.file.name)
      this.uploadButtonLoading = true
      api.learningSession.upload(formData, (response) => {
        const {data} = response.data
        if (data.length > 0) {
          this.$toast.show(`존재하지 않는 학생 코드가 존재합니다. \n 학생 코드 : ${data.toString()}`,
            {
              position: "top-right",
              duration: 12000
            });
        }
        this.currentPage = 1
        this.updateQueryParams()
        this.get(1)
        this.$refs.fileInput.value = null;
        this.uploadButtonLoading = false
      }, (e) => {
        console.error(e)
      })
    },

    addUpload(event) {
      const uploadFile = event.target.files[0];
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel' // .xls
      ];

      if (!validTypes.includes(uploadFile.type)) {
        this.$toast.show('엑셀 형식만 올릴 수 있습니다.',
          {
            position: "top-right",
            duration: 2000
          });
        event.target.value = null;
        return
      } else {
        this.file = uploadFile
      }

      let formData = new FormData()
      formData.append('file', this.file, this.file.name)
      this.uploadButtonLoading = true
      api.learningSession.uploadAdd(formData, (response) => {
        const {data} = response.data
        if (data.length > 0) {
          this.$toast.show(`존재하지 않는 학생 코드가 존재합니다. \n 학생 코드 : ${data.toString()}`,
            {
              position: "top-right",
              duration: 12000
            });
        }
        this.currentPage = 1
        this.updateQueryParams()
        this.get(1)
        this.$refs.addFileInput.value = null;
        this.uploadButtonLoading = false
      }, (e) => {
        console.error(e)
      })
    },

    deleteItem(id) {
      this.isLoading = true
      api.learningSession.delete(id, () => {
        this.get(this.currentPage)
      }, (error) => {
        console.log(error)
      })
    },

    getParams(page) {
      const params = {
        accessCodePaymentType: this.accessCodePaymentType,
        searchKeyword: this.search.keyword,
        sessionNumber: this.search.sessionNumber,
        searchStartDt: (this.search.searchStartDate && moment(this.search.searchStartDate).format('yyyy-MM-DD HH:mm:ss')) || null,
        searchEndDt: (this.search.searchEndDate && moment(this.search.searchEndDate).format('yyyy-MM-DD HH:mm:ss')) || null,
        roundInSession: this.search.roundInSession,
        loginStatus: this.search.loginStatus,
        page: page,
      }

      // 수업 시작 필터 파라미터 추가
      if (this.search.startLessonStatus.status) {
        params.startLessonStatus = this.search.startLessonStatus.status;

        // 수업 시작 O이고 날짜가 설정된 경우
        if (this.search.startLessonStatus.status === 'started') {
          if (this.search.startLessonStatus.startDate) {
            params.startLessonStartDate = moment(this.search.startLessonStatus.startDate).format('yyyy-MM-DD');
          }
          if (this.search.startLessonStatus.endDate) {
            params.startLessonEndDate = moment(this.search.startLessonStatus.endDate).format('yyyy-MM-DD');
          }
        }
      }

      // 기존 milestone 필터 (호환성을 위해 유지)
      if (this.search.milestone && this.search.milestone.includes("X")) {
        params.notExistMileStone = this.search.milestone.replace("-X", "")
      } else {
        params.milestone = this.search.milestone
      }

      // 새로운 미션 필터들 추가
      const activeMissionFilters = {};
      Object.entries(this.search.missionFilters).forEach(([missionId, filter]) => {
        const hasActiveFilter = filter.status || filter.periodCondition;
        if (hasActiveFilter) {
          activeMissionFilters[missionId] = {
            status: filter.status,
            periodCondition: filter.periodCondition,
            customStartDate: filter.customStartDate ? moment(filter.customStartDate).format('yyyy-MM-DD') : null,
            customEndDate: filter.customEndDate ? moment(filter.customEndDate).format('yyyy-MM-DD') : null
          };
        }
      });

      if (Object.keys(activeMissionFilters).length > 0) {
        params.missionFilters = activeMissionFilters;
      }

      return params
    },

    download() {
      const params = this.getParams()
      this.downloadButtonLoading = true
      api.learningSession.download(params, (response) => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `학습관리내역_${new Date().toISOString()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        this.downloadButtonLoading = false
      })
    },

    showDeletePopup() {
      this.openModal({
        type: 'confirm',
        isOpen: true,
        title:'학습 관리 일괄 삭제',
        html: '<p class="text-rose-600 break-words">삭제 처리를 할 경우, 모든 학습관리 데이터가 사라지며 학생 수강페이지 내의 마감일 안내도 모두 사라지게 됩니다. <br/> 삭제하시겠습니다?</p>',
        confirm: () => {
          this.$refs.deleteFileInput.click()
        },
      });
    },

    deleteAll() {
      const deleteFile = event.target.files[0];
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel' // .xls
      ];

      if (!validTypes.includes(deleteFile.type)) {
        this.$toast.show('엑셀 형식만 올릴 수 있습니다.',
          {
            position: "top-right",
            duration: 2000
          });
        event.target.value = null;
        return
      }

      let formData = new FormData()
      formData.append('file', deleteFile, deleteFile.name)
      this.deleteButtonLoading = true
      api.learningSession.deleteAll(formData, (response) => {
        this.currentPage = 1
        this.updateQueryParams()
        this.get(1)
        this.$refs.deleteFileInput.value = null;
        this.deleteButtonLoading = false
        const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `학습관리내역_${new Date().toISOString()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        this.downloadButtonLoading = false
      }, (e) => {
        console.error(e)
        const message = e?.response?.data?.message || "삭제 실패했습니다."
        this.openModal({
          type: 'error',
          isOpen: true,
          title:'학습 관리 일괄 삭제 에러',
          html: message,
          confirm: () => {
            this.$refs.deleteFileInput.value = null;
            this.deleteButtonLoading = false
          },
        });
        
      })
    },

    get(page = 1) {
      this.isLoading = true
      this.learningSessions = []
      const params = this.getParams(page)
      api.learningSession.search(params, (response) => {
        const {content, totalElements, totalPages} = response.data
        this.learningSessions = content
        this.totalRows = totalElements
        this.totalPage = totalPages
        this.currentPage = page
        this.isLoading = false
      }, (err) => {
        console.log(err)
        this.isLoading = false
      })
    },
  },
}
</script>
